import moment from 'moment';
import { Phase } from '../store/configStore';

export type AppStatus = 'SIGNUP' | 'QUALIFICATION' | 'FINALE' | 'CLOSED';

export const APP_STATUS: Record<AppStatus, AppStatus> = {
    SIGNUP: 'SIGNUP',
    QUALIFICATION: 'QUALIFICATION',
    FINALE: 'FINALE',
    CLOSED: 'CLOSED',
};

export interface LegacyPhase {
    type: 'fingerprint' | 'sms';
    start: string;
    end: string;
    phaseType: 'fingerprint' | 'sms';
    phaseStart: string;
    phaseEnd: string;
    phaseOrder: number;
}

export interface ProcessedPhases {
    appStatus: AppStatus;
    phaseDate: string | null;
    countdownMessage: string;
    campaignLaunch: string | null;
    voteStart: string | null;
    voteStartFinale: string | null;
    voteEnd: string | null;
    campaignLaunched: boolean;
    voteStarted: boolean;
    voteStartedFinale: boolean;
    voteEnded: boolean;
    signUpActive: boolean;
    hasQualification: boolean;
    hasFinale: boolean;
    type: LegacyPhase[];
}

/**
 * Transforms the new backend phase configuration into a frontend-friendly status object.
 */
export const transformPhases = (phases?: Phase[]): ProcessedPhases => {
    // Return safe defaults if phases are missing
    if (!phases || !Array.isArray(phases) || phases.length === 0) {
        return {
            appStatus: APP_STATUS.SIGNUP,
            phaseDate: null,
            countdownMessage: '',
            campaignLaunch: null,
            voteStart: null,
            voteStartFinale: null,
            voteEnd: null,
            campaignLaunched: false,
            voteStarted: false,
            voteStartedFinale: false,
            voteEnded: false,
            signUpActive: false,
            hasQualification: false,
            hasFinale: false,
            type: [],
        };
    }

    const now = moment();

    const signupPhase = phases.find((p) => p.type === 'signup');
    const qualificationPhase = phases.find((p) => p.type === 'qualification');
    const finalePhase = phases.find((p) => p.type === 'finale');

    const launchDate = signupPhase ? signupPhase.start : null;
    const signupEnd = signupPhase ? signupPhase.end : null;
    const voteStart = qualificationPhase ? qualificationPhase.start : finalePhase ? finalePhase.start : null;
    const voteStartFinale = finalePhase ? finalePhase.start : null;
    const voteEnd = finalePhase ? finalePhase.end : qualificationPhase ? qualificationPhase.end : null;

    const campaignLaunched = launchDate ? now.isSameOrAfter(moment(launchDate)) : false;
    const signUpActive = signupPhase ? now.isSameOrAfter(moment(launchDate)) && now.isBefore(moment(signupEnd)) : false;
    const voteStarted = voteStart ? now.isSameOrAfter(moment(voteStart)) : false;
    const voteStartedFinale = voteStartFinale ? now.isSameOrAfter(moment(voteStartFinale)) : false;
    const voteEnded = voteEnd ? now.isSameOrAfter(moment(voteEnd)) : false;

    // Determine current App Status
    let appStatus: AppStatus = APP_STATUS.SIGNUP;
    if (voteEnded) {
        appStatus = APP_STATUS.CLOSED;
    } else if (voteStartedFinale) {
        appStatus = APP_STATUS.FINALE;
    } else if (voteStarted) {
        appStatus = APP_STATUS.QUALIFICATION;
    } else if (!signUpActive && campaignLaunched) {
        // Signup finished but voting not yet started
        appStatus = APP_STATUS.CLOSED;
    }

    // Helper for Countdown.js
    let phaseDate: string | null = '';
    let countdownMessage = '';

    if (!voteStarted) {
        phaseDate = voteStart;
        countdownMessage = 'bis zur Abstimmung';
    } else if (!voteEnded) {
        if (qualificationPhase && finalePhase) {
            if (voteStartedFinale) {
                phaseDate = voteEnd;
                countdownMessage = 'bis zum Ende';
            } else {
                phaseDate = voteStartFinale;
                countdownMessage = 'bis zum Finale';
            }
        } else {
            phaseDate = voteEnd;
            countdownMessage = 'bis zum Ende';
        }
    } else {
        countdownMessage = 'ist beendet';
        phaseDate = null;
    }

    // Map to legacy 'type' array for compatibility if needed
    const typeArray: LegacyPhase[] = [];
    if (qualificationPhase) {
        typeArray.push({
            type: 'fingerprint', // Legacy name for qualification
            start: qualificationPhase.start,
            end: qualificationPhase.end,
            phaseType: 'fingerprint',
            phaseStart: qualificationPhase.start,
            phaseEnd: qualificationPhase.end,
            phaseOrder: 0,
        });
    }
    if (finalePhase) {
        typeArray.push({
            type: 'sms', // Legacy name for finale
            start: finalePhase.start,
            end: finalePhase.end,
            phaseType: 'sms',
            phaseStart: finalePhase.start,
            phaseEnd: finalePhase.end,
            phaseOrder: qualificationPhase ? 1 : 0,
        });
    }

    return {
        appStatus,
        phaseDate,
        countdownMessage,
        campaignLaunch: launchDate,
        voteStart,
        voteStartFinale,
        voteEnd,
        campaignLaunched,
        voteStarted,
        voteStartedFinale,
        voteEnded,
        signUpActive,
        hasQualification: !!qualificationPhase,
        hasFinale: !!finalePhase,
        type: typeArray,
    };
};
